import React, { useState, useEffect, memo, useMemo } from 'react';
import { shallowEqual } from 'react-redux';
import { UploadOutlined } from '@ant-design/icons';
import { Alert, Upload, Button, message, Select } from 'antd';
import { Modal } from 'antd';
import axios from 'axios';
import { useFormik } from 'formik';
import produce from 'immer';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { useCampaign } from 'api/campaigns';
import { FEATURES, TENANTS } from 'appConstants';
import { FormItem } from 'components/Layout/Form';
import { CampaignSelect, LoyaltyProgramSelect } from 'components/selects';
import { TCampaignReviewPage, TReferralBulkActions } from 'containers/Campaign/i18n/TModules';
import { useAuthUser, useFeatureFlags, useTenant, useTypedSelector } from 'hooks';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';
import { cdnURL } from 'utils/helpers';
import API from 'utils/request';

export const REVERSE_RAFFLE_TICKETS = 'reverse_raffle_tickets';
export const ISSUE_RAFFLE_TICKETS = 'issue_raffle_tickets';

export const getFileUploadSampleLink = (type: string, tenantName: string) => {
  switch (type) {
    case 'upload_merchants':
      if (tenantName === TENANTS?.SIAM_PIWAT || tenantName === TENANTS?.SIAM_PIWAT_PREPROD) {
        return cdnURL('sample_spw_upload_merchant.csv');
      } else {
        return cdnURL(`sample_${type}.csv`);
      }
    case 'upload_users':
      if (tenantName === TENANTS?.GLONEONE) {
        return cdnURL(`sample_globeone_${type}.csv`);
      } else {
        return cdnURL(`sample_${type}.csv`);
      }
    case 'issue_vouchers':
    case 'upload_transactions':
    case 'upload_tries':
    case REVERSE_RAFFLE_TICKETS:
    case ISSUE_RAFFLE_TICKETS:
      return cdnURL(`sample_${type}.csv`);
    case 'update_vouchers':
      return cdnURL(`update_voucher_status_sample.csv`);
    case 'upload_vouchers':
      return cdnURL(`sample_upload_vouchers.csv`);
    case 'bulk_enrol_users':
    case 'bulk_unenrol_users':
      return cdnURL(`sample_bulk_enrol.csv`);
    case 'create_points':
    case 'upload_points':
      return cdnURL(`sample_bulk_upload_points.csv`);
    case 'bulk_update_voucher_expiry':
      return cdnURL(`sample_bulk_update_voucher_expiry.csv`);
    case 'upload_loyalty_points':
      return cdnURL(`sample_bulk_upload_loyalty_points.csv`);
    case 'upload_triggers':
      return cdnURL(`sample_upload_triggers.csv`);
    case 'upload_audience':
      return cdnURL(`sample_upload_audience.csv`);
    case 'lookup_table_values':
      return cdnURL(`upload_lookup_table_sample.xlsx`);
    case 'upload_referee_users':
      return cdnURL('sample_upload_referee_users.csv');
    case 'upload_referral_count':
      return cdnURL('sample_upload_referral_count.csv');
    default:
      return cdnURL('bulk_issue_sample.csv');
  }
};

const BulkActionModal = ({
  isOpen,
  closeModal,
  campaignId = null,
  onSuccess = () => {},
  onError = () => {},
  onCancel
}) => {
  const { values, setFieldValue } = useFormik({
    onSubmit: () => {}, //Just a placeholder to prevent TS error
    initialValues: {
      campaign_id: campaignId,
      loyalty_program_id: null
    }
  });
  const featureFlags = useFeatureFlags();
  const uploadUsersEnabled = featureFlags[FEATURES.BULK_ACTIONS_UPLOAD_USERS.key];
  const reversalEnabled = featureFlags[FEATURES.RAFFLE_TICKET_REVERSAL.key];
  const issueRaffleTicketsEnabled = featureFlags[FEATURES.BULK_ISSUE_RAFFLE_TICKETS.key];
  const makerCheckerBulkAction = featureFlags[FEATURES.MAKER_CHECKER_CUSTOMER_BULK_ACTIONS.key];
  const referralAggregatedCountUploadEnabled =
    featureFlags[FEATURES.REFERRAL_AGGREGATED_COUNT_BULK_UPLOAD.key];
  const inviteCampaignEnabled = featureFlags[FEATURES.INVITE.key];
  const actions = useTypedSelector(state => state.global.bulkActions, shallowEqual);

  const authUser = useAuthUser();
  const roles = authUser?.roles || [];
  const isRoleTenantAdmin = roles.some(role =>
    ['tenant admin', 'Tenant Admin'].includes(role.name)
  );

  const { t } = useMultiLanguageTranslation();

  const ACCEPTS_FILES = useMemo(
    () => ({
      uob_uob: {
        accept:
          '.csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel',
        label: t(`${TCampaignReviewPage}.BulkActionModal.AcceptedFormatXlsxOrCsvFilesOnly`)
      }
    }),
    [t]
  );

  const DEFAULT_ACCEPT_FILE = useMemo(
    () => ({
      accept:
        '.csv, .txt, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel',
      label: t(`${TCampaignReviewPage}.BulkActionModal.AcceptedFormatTxtXlsxOrCsvFilesOnly`)
    }),
    [t]
  );

  const bulkActions = useMemo(() => {
    let next = actions;
    if (!uploadUsersEnabled) {
      next = next.filter(action => action.file_type !== 'upload_users');
    }
    if (!referralAggregatedCountUploadEnabled) {
      next = next.filter(action => action.file_type !== 'upload_referral_count');
    }
    if (!inviteCampaignEnabled) {
      next = next.filter(action => action.file_type !== 'upload_referee_users');
    }
    if (!reversalEnabled) {
      next = next.filter(action => action.file_type !== REVERSE_RAFFLE_TICKETS);
    }
    if (!issueRaffleTicketsEnabled) {
      next = next.filter(action => action.file_type !== ISSUE_RAFFLE_TICKETS);
    }
    return next;
  }, [
    uploadUsersEnabled,
    referralAggregatedCountUploadEnabled,
    inviteCampaignEnabled,
    reversalEnabled,
    issueRaffleTicketsEnabled,
    actions
  ]);

  const [type, setType] = useState(
    campaignId !== null
      ? 'upload_tries'
      : bulkActions && bulkActions[0]
      ? bulkActions[0].file_type
      : undefined
  );
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const { name: tenantName } = useTenant();
  const isRaffleReversal = type === REVERSE_RAFFLE_TICKETS;
  const isRaffleIssuance = type === ISSUE_RAFFLE_TICKETS;
  const isRaffleType = isRaffleReversal || isRaffleIssuance;
  const campaignSelectable =
    type === 'upload_tries' || type === 'upload_referral_count' || isRaffleType;
  const loyaltyProgramSelectable = type === 'upload_loyalty_rules' || type === 'upload_points';

  const isReferralCountUpload = type === 'upload_referral_count';
  const { data: selectedCampaign } = useCampaign(
    isReferralCountUpload && values.campaign_id ? values.campaign_id : null
  );
  const selectedCampaignHasRules = useMemo(() => {
    if (!isReferralCountUpload || !selectedCampaign) return false;
    // `properties.tier_ladder` is the redux-form shape; the campaign endpoint returns tiers
    // at the top level with the rule under `transaction_rule`.
    const tiers = (selectedCampaign as { tiers?: any[] })?.tiers;
    if (!Array.isArray(tiers)) return false;
    return tiers.some((t: any) => Boolean(t?.transaction_rule ?? t?.rules?.[0] ?? t?.rule));
  }, [isReferralCountUpload, selectedCampaign]);

  const handleUpload = async e => {
    if (uploading) return;

    const { campaign_id, loyalty_program_id } = values;

    setUploading(true);

    const formData = new FormData();
    formData.append('type', type);
    formData.append('file[filename]', fileList[0].name);
    formData.append('file[file]', fileList[0]);

    if (campaignSelectable && campaign_id) {
      formData.append('upload_params[campaign_id]', campaign_id);
    }

    if (loyaltyProgramSelectable && loyalty_program_id) {
      formData.append('upload_params[loyalty_program_id]', String(loyalty_program_id));
    }

    try {
      await API.post('/v4/dash/file_imports', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      setUploading(false);
      const baseMessage =
        makerCheckerBulkAction && !isRoleTenantAdmin
          ? t(`${TCampaignReviewPage}.BulkActionModal.FileUploadedIsSubmittedForApproval`)
          : t(`${TCampaignReviewPage}.BulkActionModal.FileUploaded`);
      message.success(baseMessage);
      onSuccess();
      handleCancel();
    } catch (err) {
      if (!axios.isCancel(err)) {
        setUploading(false);
        const status = err?.response?.status;
        const showsApiMessage =
          (isRaffleReversal && status === 422) ||
          (isRaffleIssuance && (status === 422 || status === 404));
        if (showsApiMessage) {
          message.error(
            err.response.data?.message ||
              t(`${TCampaignReviewPage}.BulkActionModal.ErrorUploadingFile`)
          );
          onError();
          return;
        }
        message.error(t(`${TCampaignReviewPage}.BulkActionModal.ErrorUploadingFile`));
        onError();
      }
    }
  };

  useEffect(() => {
    if (!campaignSelectable) {
      setFieldValue('campaign_id', undefined);
    }
    if (!loyaltyProgramSelectable) {
      setFieldValue('loyalty_program_id', undefined);
    }
  }, [setFieldValue, campaignSelectable, loyaltyProgramSelectable]);

  useEffect(() => {
    if (campaignId) {
      setType('upload_tries');
      setFieldValue('campaign_id', campaignId);
    }
  }, [setFieldValue, campaignId]);

  // upload_tries picks game and stamp campaigns while the raffle types pick active raffles, so
  // switching type has to drop the previously chosen campaign.
  useEffect(() => {
    if (!campaignId) {
      setFieldValue('campaign_id', undefined);
    }
  }, [type, campaignId, setFieldValue]);

  const fileUpload = ACCEPTS_FILES[tenantName] || DEFAULT_ACCEPT_FILE;

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    closeModal();
    setFieldValue('campaign_id', undefined);
    setType(
      campaignId !== null
        ? 'upload_tries'
        : bulkActions && bulkActions[0]
        ? bulkActions[0].file_type
        : undefined
    );
    setFileList([]);
  };

  return (
    <Modal
      maskClosable={false}
      open={isOpen}
      title={t(`${TCampaignReviewPage}.BulkActionModal.BulkUpload`)}
      onCancel={handleCancel}
      onOk={handleUpload}
      okText={t(`${TCampaignReviewPage}.BulkActionModal.Upload`)}
      footer={[
        <Button key="back" onClick={handleCancel}>
          {t(`${TCampaignReviewPage}.BulkActionModal.Cancel`)}
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={uploading}
          onClick={handleUpload}
          disabled={
            uploading ||
            fileList.length === 0 ||
            (campaignSelectable && !values.campaign_id) ||
            (loyaltyProgramSelectable && !values.loyalty_program_id)
          }
        >
          {t(`${TCampaignReviewPage}.BulkActionModal.Upload`)}
        </Button>
      ]}
      cancelText={t(`${TCampaignReviewPage}.BulkActionModal.Cancel`)}
    >
      <form onSubmit={handleUpload} aria-label="form" data-testid="bulk-action-modal-form">
        {!campaignId && (
          <>
            <FormItem label={t(`${TCampaignReviewPage}.BulkActionModal.ActionType`)}>
              <Select
                value={type}
                onChange={setType}
                placeholder={t(`${TCampaignReviewPage}.BulkActionModal.ChooseOne`)}
                allowClear
                data-testid="file_type-select"
              >
                {bulkActions?.map(item => (
                  <Select.Option value={item.file_type} key={item.file_type}>
                    {item.file_name}
                  </Select.Option>
                ))}
              </Select>
            </FormItem>
            {campaignSelectable && (
              <FormItem
                label={t(`${TCampaignReviewPage}.BulkActionModal.Campaign`)}
                htmlFor="campaign_id"
                required={isRaffleIssuance}
              >
                <CampaignSelect
                  id="campaign_id"
                  showArrow
                  showSearch
                  showId
                  value={values.campaign_id}
                  onChange={value => setFieldValue('campaign_id', value)}
                  placeholder={t(`${TCampaignReviewPage}.BulkActionModal.PleaseSelectACampaign`)}
                  params={
                    isRaffleType
                      ? { campaign_type: ['raffle'], state: 'active' }
                      : {
                          campaign_type: isReferralCountUpload ? ['invite'] : ['game', 'stamp']
                        }
                  }
                  data-testid="campaign-select"
                />
              </FormItem>
            )}
            {loyaltyProgramSelectable && (
              <FormItem label={t(`${TCampaignReviewPage}.BulkActionModal.LoyaltyProgram`)}>
                <LoyaltyProgramSelect
                  value={values.loyalty_program_id}
                  onChange={value => setFieldValue('loyalty_program_id', value)}
                  data-testid="loyalty-select"
                  placeholder={t(
                    `${TCampaignReviewPage}.BulkActionModal.PleaseSelectALoyaltyProgram`
                  )}
                />
              </FormItem>
            )}
          </>
        )}

        {selectedCampaignHasRules && (
          <Alert
            type="error"
            showIcon
            data-testid="upload-referral-count-rule-bypass-warning"
            message={t(`${TReferralBulkActions}.RuleBypassWarning`)}
            style={{ marginBottom: 16 }}
          />
        )}

        {type && (
          <UploadWrapper isFileSelected={fileList.length > 0}>
            <Upload.Dragger
              accept={fileUpload.accept}
              name="file"
              beforeUpload={file => {
                if (fileList.length < 1) {
                  setFileList(prev => [...prev, file]);
                }
                return false;
              }}
              fileList={fileList}
              onRemove={file => {
                setFileList(
                  produce(prev => {
                    const index = prev.indexOf(file);
                    prev.splice(index, 1);
                  })
                );
              }}
              style={{ marginBottom: 16 }}
            >
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p className="ant-upload-text">
                {t(`${TCampaignReviewPage}.BulkActionModal.ClickOrDragFileToThisAreaToUpload`)}
              </p>
            </Upload.Dragger>
            <div>
              <a href={getFileUploadSampleLink(type, tenantName)}>
                {t(`${TCampaignReviewPage}.BulkActionModal.DownloadSample`)}
              </a>
              <div>{fileUpload.label}</div>
            </div>
          </UploadWrapper>
        )}
      </form>
    </Modal>
  );
};

const UploadWrapper = styled.div<{ isFileSelected: boolean }>`
  .ant-upload,
  .ant-upload-drag {
    display: ${props => (props.isFileSelected ? 'none' : 'block')};
  }
`;

BulkActionModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  campaignId: PropTypes.number,
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
  onCancel: PropTypes.func
};

export default memo(BulkActionModal);
